export interface FlatSortMenuRow {
  id: string
  parentId?: string | null
  orderId?: number
  name?: string
  title?: string
}

export interface SortTreeNode {
  id: string
  parentId: string | null
  name?: string
  title?: string
  children: Array<SortTreeNode>
}

/**
 * Builds a nested tree of arbitrary depth from a flat {id, parentId, orderId}
 * list, ordering each parent's children by orderId. A row whose parentId
 * doesn't resolve to any row in the set (a dangling reference to a deleted
 * menu) is treated — and re-persisted — as top-level, rather than being
 * dropped from the tree.
 */
export const buildSortTree = (rows: Array<FlatSortMenuRow>): Array<SortTreeNode> => {
  const byId = new Map<string, SortTreeNode>()
  rows.forEach((row) => {
    byId.set(row.id, {
      id: row.id,
      parentId: row.parentId ?? null,
      name: row.name,
      title: row.title,
      children: [],
    })
  })

  const roots: Array<SortTreeNode> = []
  const sortedRows = [...rows].sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0))

  sortedRows.forEach((row) => {
    const node = byId.get(row.id) as SortTreeNode
    const parentId = row.parentId ?? null
    const parent = parentId ? byId.get(parentId) : undefined

    if (parent) {
      parent.children.push(node)
    } else {
      if (parentId) node.parentId = null
      roots.push(node)
    }
  })

  return roots
}

/**
 * Inverse of buildSortTree: flattens a (possibly reordered) tree back to the
 * {menuId, parentId} wire shape PUT /menu/sort expects, in pre-order — a
 * parent is immediately followed by its own children, matching how the
 * backend assigns order_id from array position.
 */
export const flattenSortTree = (
  tree: Array<SortTreeNode>
): Array<{menuId: string; parentId: string | null}> => {
  const result: Array<{menuId: string; parentId: string | null}> = []

  const visit = (nodes: Array<SortTreeNode>) => {
    nodes.forEach((node) => {
      result.push({menuId: node.id, parentId: node.parentId})
      if (node.children.length > 0) visit(node.children)
    })
  }

  visit(tree)
  return result
}

/**
 * Replaces one node's children array immutably, wherever it is in the tree.
 * Used to commit a reorder that happened within a single nested level's
 * SortableContext (top-level reordering is handled separately, via
 * reorderSubsetInPlace, since it's the virtualized/searchable level).
 */
export const updateNodeChildren = (
  tree: Array<SortTreeNode>,
  targetId: string,
  newChildren: Array<SortTreeNode>
): Array<SortTreeNode> =>
  tree.map((node) => {
    if (node.id === targetId) return {...node, children: newChildren}
    if (node.children.length === 0) return node
    return {...node, children: updateNodeChildren(node.children, targetId, newChildren)}
  })

/** True if the node itself, or any of its descendants, matches the query. */
export const treeNodeMatchesQuery = (node: SortTreeNode, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const label = (node.name ?? node.title ?? '').toLowerCase()
  if (label.includes(q)) return true
  return node.children.some((child) => treeNodeMatchesQuery(child, q))
}

/**
 * Moves the item at fromIndex to toIndex, shifting everything between them.
 * toIndex is clamped to the array's valid range, so an out-of-range typed
 * position (e.g. "999" in a 12-item list) lands on the last item instead of
 * being silently ignored or throwing.
 */
export const moveItem = <T>(array: Array<T>, fromIndex: number, toIndex: number): Array<T> => {
  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= array.length) return array
  const clampedTo = Math.max(0, Math.min(toIndex, array.length - 1))
  const result = [...array]
  const [moved] = result.splice(fromIndex, 1)
  result.splice(clampedTo, 0, moved)
  return result
}
